import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Download, DollarSign, Plus, CheckCircle, Loader2, Trash2, Globe } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { formatSydney, formatTableDateSydney, getCurrentSydneyDisplay } from "../../utils/timezone";

export default function AdminSignups() {
  const { api } = useAuth();
  const [signups, setSignups] = useState([]);
  const [summary, setSummary] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [commissionDialog, setCommissionDialog] = useState(false);
  const [savingCommission, setSavingCommission] = useState(false);
  const [serverTime, setServerTime] = useState(null);
  
  const [filters, setFilters] = useState({
    user_id: "all",
    start_date: new Date(new Date().setDate(new Date().getDate() - 30)),
    end_date: new Date(),
  });
  
  const [commissionForm, setCommissionForm] = useState({
    user_id: "",
    period_start: new Date(new Date().setDate(new Date().getDate() - 30)),
    period_end: new Date(),
    commission_rate: 10,
    notes: "",
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [signupsRes, summaryRes, usersRes, timeRes] = await Promise.all([
        api().get("/admin/signups", {
          params: {
            user_id: filters.user_id === "all" ? undefined : filters.user_id,
            start_date: format(filters.start_date, "yyyy-MM-dd"),
            end_date: format(filters.end_date, "yyyy-MM-dd"),
          },
        }),
        api().get("/admin/signups/summary", {
          params: {
            start_date: format(filters.start_date, "yyyy-MM-dd"),
            end_date: format(filters.end_date, "yyyy-MM-dd"),
          },
        }),
        api().get("/admin/users"),
        api().get("/server-time"),
      ]);
      setSignups(signupsRes.data);
      setSummary(summaryRes.data);
      setUsers(usersRes.data.filter(u => u.role === "staff"));
      setServerTime(timeRes.data);
    } catch (error) {
      toast.error("Failed to load signups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api().get("/admin/export/signups", {
        params: {
          start_date: format(filters.start_date, "yyyy-MM-dd"),
          end_date: format(filters.end_date, "yyyy-MM-dd"),
        },
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `signups_${format(filters.start_date, "yyyy-MM-dd")}_${format(filters.end_date, "yyyy-MM-dd")}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Signups exported!");
    } catch (error) {
      toast.error("Failed to export signups");
    } finally {
      setExporting(false);
    }
  };

  const handleCreateCommission = async (e) => {
    e.preventDefault();
    if (!commissionForm.user_id) {
      toast.error("Please select a staff member");
      return;
    }
    
    setSavingCommission(true);
    try {
      await api().post("/admin/commissions", {
        user_id: commissionForm.user_id,
        period_start: format(commissionForm.period_start, "yyyy-MM-dd"),
        period_end: format(commissionForm.period_end, "yyyy-MM-dd"),
        commission_rate: parseFloat(commissionForm.commission_rate),
        notes: commissionForm.notes,
      });
      toast.success("Commission calculated and email sent to staff!");
      setCommissionDialog(false);
      setCommissionForm({
        user_id: "",
        period_start: new Date(new Date().setDate(new Date().getDate() - 30)),
        period_end: new Date(),
        commission_rate: 10,
        notes: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create commission");
    } finally {
      setSavingCommission(false);
    }
  };

  const handleDeleteSignup = async (signupId) => {
    try {
      await api().delete(`/admin/signups/${signupId}`);
      toast.success("Signup deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete signup");
    }
  };

  const totalAmount = signups.reduce((sum, s) => sum + (s.amount || 0), 0);

  return (
    <div className="space-y-8" data-testid="admin-signups">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Signup Tracking</h1>
          <p className="text-slate-500 mt-1">View all staff signups and calculate commissions</p>
        </div>
        
        <div className="flex gap-2">
          <Dialog open={commissionDialog} onOpenChange={setCommissionDialog}>
            <DialogTrigger asChild>
              <Button className="bg-purple-600 hover:bg-purple-700" data-testid="calculate-commission-btn">
                <Plus className="mr-2 h-4 w-4" />
                Calculate Commission
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Calculate Commission</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCommission} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Staff Member *</Label>
                  <Select
                    value={commissionForm.user_id}
                    onValueChange={(value) => setCommissionForm({ ...commissionForm, user_id: value })}
                  >
                    <SelectTrigger data-testid="commission-staff-select">
                      <SelectValue placeholder="Select staff" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Period Start</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(commissionForm.period_start, "MMM dd")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={commissionForm.period_start}
                          onSelect={(date) => date && setCommissionForm({ ...commissionForm, period_start: date })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Period End</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(commissionForm.period_end, "MMM dd")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={commissionForm.period_end}
                          onSelect={(date) => date && setCommissionForm({ ...commissionForm, period_end: date })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rate">Commission Rate (%)</Label>
                  <Input
                    id="rate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={commissionForm.commission_rate}
                    onChange={(e) => setCommissionForm({ ...commissionForm, commission_rate: e.target.value })}
                    data-testid="commission-rate-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Commission notes..."
                    value={commissionForm.notes}
                    onChange={(e) => setCommissionForm({ ...commissionForm, notes: e.target.value })}
                    rows={2}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  disabled={savingCommission}
                  data-testid="commission-submit-btn"
                >
                  {savingCommission ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Calculating...
                    </>
                  ) : "Calculate & Notify Staff"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="export-signups-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Staff Member</Label>
              <Select
                value={filters.user_id}
                onValueChange={(value) => setFilters({ ...filters, user_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(filters.start_date, "MMM dd, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.start_date}
                    onSelect={(date) => date && setFilters({ ...filters, start_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(filters.end_date, "MMM dd, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.end_date}
                    onSelect={(date) => date && setFilters({ ...filters, end_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-700 font-mono">${totalAmount.toLocaleString()}</p>
                <p className="text-sm text-emerald-600">Total Amount</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <CheckCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{signups.length}</p>
                <p className="text-sm text-slate-500">Total Signups</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-50 rounded-lg">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">
                  ${signups.length > 0 ? (totalAmount / signups.length).toFixed(2) : "0.00"}
                </p>
                <p className="text-sm text-slate-500">Avg. per Signup</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff Summary */}
      {summary.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Summary by Staff</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {summary.map((s) => (
                <div key={s.user_id} className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <p className="font-medium text-slate-900">{s.user_name}</p>
                    <p className="font-mono font-bold text-emerald-600">${s.total_amount?.toLocaleString()}</p>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{s.total_signups} signups</p>
                  {Object.keys(s.accounts || {}).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-200 space-y-1">
                      {Object.entries(s.accounts).map(([acc, data]) => (
                        <div key={acc} className="flex justify-between text-xs text-slate-500">
                          <span>{acc}</span>
                          <span>${data.amount?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signups Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">All Signups</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner w-8 h-8" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Staff</th>
                    <th>Account</th>
                    <th>Client</th>
                    <th>Amount</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {signups.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-slate-500">
                        No signups found
                      </td>
                    </tr>
                  ) : (
                    signups.map((s) => (
                      <tr key={s.id} data-testid={`signup-row-${s.id}`}>
                        <td className="font-medium">
                          {formatTableDateSydney(s.date)}
                        </td>
                        <td>{s.user_name}</td>
                        <td>{s.account_name}</td>
                        <td>{s.client_name}</td>
                        <td className="font-mono font-medium text-emerald-600">
                          ${s.amount?.toLocaleString()}
                        </td>
                        <td className="text-slate-500 text-sm max-w-[150px] truncate">
                          {s.description || "-"}
                        </td>
                        <td>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50"
                                data-testid={`delete-signup-${s.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Signup?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete the signup for {s.client_name} (${s.amount?.toLocaleString()}). This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteSignup(s.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
